import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import OnboardingHeaderV3 from "@/modules/onboarding/components/OnboardingScreenHeader/OnboardingHeaderV3";
import { updateUserIfAuthenticated } from "@/modules/onboarding/utils/updateUserIfAuthenticated";
import { useUser } from "@/modules/user/useUser/useUser";
import { Button } from "atoms/button/Button";
import { TextField } from "atoms/text-field/TextField";
import { isValidEmail } from "utils/validators";

const V3EmailPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [email, setEmail] = useState<string>(user?.email ?? "");
  const [invalid, setInvalid] = useState<boolean>(false);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user?.email]);

  const handleContinue = useCallback(async () => {
    const valid = isValidEmail(email);
    setInvalid(!valid);
    if (!valid) return;
    await updateUserIfAuthenticated({ email: email.trim() });
    const locationConfirmed =
      localStorage.getItem("locationConfirmed") === "true";
    const hasLocation = locationConfirmed || !!user?.platformLocation?.id;
    const storedPlatformId = localStorage.getItem("platformId");
    const assignedPlatformId = user?.platform?.id;
    const platformId = storedPlatformId || assignedPlatformId;
    if (hasLocation && platformId) {
      navigate("/signin/dob");
    } else if (!platformId) {
      navigate("/signin/platform-picker");
    } else {
      navigate(`/signin/location?platformId=${platformId}`);
    }
  }, [email, navigate, user?.platform?.id, user?.platformLocation?.id]);

  return (
    <MobileViewportContainer
      header={<OnboardingBackButton prev="/signin/name" />}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col gap-9"
      >
        <OnboardingHeaderV3 title="What's your email?" align="left" />
        <div className="flex flex-col gap-3">
          <TextField
            type="email"
            label="Email Address"
            placeholder="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            invalid={invalid}
            onChange={(value) => {
              setEmail(value);
              if (invalid) setInvalid(false);
            }}
            onBlur={(value) => setInvalid(!isValidEmail(value))}
            labelClassName="hidden"
            className="!ring-0 focus:!ring-0"
          />
          <div className="mt-6">
            <Button
              fullWidth
              onClick={handleContinue}
              disabled={!email || !isValidEmail(email)}
            >
              Confirm
            </Button>
          </div>
        </div>
      </motion.div>
    </MobileViewportContainer>
  );
};

export default V3EmailPage;
